//----------------------------------------------------------------------------------------------------------------------
// Graphics Utility
//----------------------------------------------------------------------------------------------------------------------

import {
    AbstractEngine,
    Engine,
    type EngineOptions,
    NullEngine,
    type NullEngineOptions,
    WebGPUEngine,
    type WebGPUEngineOptions,
} from '@babylonjs/core';

// Interfaces
import type { BabylonEngineOptions, GameCanvas, RenderEngineOptions } from '../interfaces/game.ts';

// Utilities
import { hasWebGPU } from './capabilities.ts';

//----------------------------------------------------------------------------------------------------------------------

/**
 * Builds and initializes a WebGPU engine.
 *
 * @param canvas
 * @param options
 */
async function _buildWebGPUEngine(
    canvas : HTMLCanvasElement | OffscreenCanvas,
    options : WebGPUEngineOptions
) : Promise<WebGPUEngine>
{
    const engine = new WebGPUEngine(canvas, options);
    await engine.initAsync();
    return engine;
}

/**
 * Builds a WebGL engine.
 *
 * @param canvas
 * @param options
 */
function _buildWebGLEngine(canvas : GameCanvas, options : BabylonEngineOptions) : Engine
{
    return new Engine(canvas, options.antialias, options.options, options.adaptToDeviceRatio);
}

/**
 * Builds a Null engine, which is used when no rendering is required.
 *
 * @param options
 */
function _buildNullEngine(options : NullEngineOptions) : NullEngine
{
    return new NullEngine(options as NullEngineOptions);
}

//------------------------------------------------------------------------------------------------------------------

/**
 * Creates an appropriate engine based on the provided canvas and options.
 * Passing null for canvas creates a NullEngine. Otherwise auto-detects WebGPU with WebGL fallback,
 * unless a specific engine type is forced via `options.engine`.
 *
 * @param canvas - Pass null to get a NullEngine (headless/testing)
 * @param options
 */
export async function createEngine(
    canvas : GameCanvas | null,
    options : RenderEngineOptions,
    largeWorldRendering = false
) : Promise<AbstractEngine>
{
    // Check if we should use a null engine (no canvas provided)
    if(canvas === null)
    {
        console.debug('Using Null Engine');
        return _buildNullEngine(options as NullEngineOptions);
    }

    // Inject large world rendering into engine options if requested. We have to build new objects
    // here because BabylonJS declares useLargeWorldRendering as readonly on its option types.
    if(largeWorldRendering)
    {
        if('options' in options)
        {
            const babylonOpts = options as BabylonEngineOptions;
            options = {
                ...babylonOpts,
                options: { ...babylonOpts.options, useLargeWorldRendering: true },
            } as RenderEngineOptions;
        }
        else
        {
            // WebGPUEngineOptions -- useLargeWorldRendering lives at the top level
            options = { ...options, useLargeWorldRendering: true } as RenderEngineOptions;
        }
    }

    // Extract the forceEngine option if available
    const forceEngine = options.engine || 'auto';

    // Force WebGPU engine if specified and available
    if(forceEngine === 'webgpu')
    {
        if(hasWebGPU())
        {
            try
            {
                console.debug('Using forced WebGPU engine');
                return await _buildWebGPUEngine(canvas, options);
            }
            catch (error)
            {
                console.error('Forced WebGPU initialization failed:', error);
                throw new Error(
                    'Forced WebGPU failed to initialize. If WebGPU is required, check browser compatibility.',
                    { cause: error }
                );
            }
        }
        else
        {
            throw new Error('WebGPU was forced but is not available in this browser.');
        }
    }

    // Force WebGL engine if specified
    if(forceEngine === 'webgl')
    {
        console.debug('Using forced WebGL engine');
        return _buildWebGLEngine(canvas, options as BabylonEngineOptions);
    }

    // Auto detection (default behavior)
    if(hasWebGPU())
    {
        try
        {
            console.debug('Using WebGPU');

            return _buildWebGPUEngine(canvas, options)
                .catch((error) =>
                {
                    console.warn('WebGPU initialization failed, falling back to WebGL:', error);
                    return _buildWebGLEngine(canvas, options as EngineOptions);
                });
        }
        catch (error)
        {
            console.warn('WebGPU initialization failed, falling back to WebGL:', error);
        }
    }
    else
    {
        console.warn('WebGPU not supported, falling back to WebGL.');
    }

    console.debug('Using WebGL');

    return _buildWebGLEngine(canvas, options as EngineOptions);
}

//----------------------------------------------------------------------------------------------------------------------

